import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { AuthService, GenericError } from '@auth0/auth0-angular';
import { UIThemeService } from '@bannerflow/ui';
import { SentinelService } from '@bannerflow/sentinel';
import { FULLSTORY_CONFIG, FullstoryService } from '@bannerflow/fullstory';
import { EMPTY, catchError, filter, switchMap, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppComponentStore } from './app.component.store';
import { AccountAccessService } from './core/services/account-access.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [AppComponentStore],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent {
  public title = 'video-upload';
  private sentinelService = inject(SentinelService);
  private accountAccessService = inject(AccountAccessService);
  private fullstoryService = inject(FullstoryService);
  private fullstoryEnabled = inject(FULLSTORY_CONFIG).enabled;
  constructor(
    private uiThemeService: UIThemeService,
    private authService: AuthService,
  ) {
    this.authService.error$
      .pipe(
        filter(
          (e) =>
            e instanceof GenericError &&
            (e.error === 'login_required' ||
              e.error === 'invalid_grant' ||
              e.error === 'missing_refresh_token'),
        ),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.authService.loginWithRedirect();
      });

    // Resolving the identity costs two Account Access round-trips per login, and
    // `identify` returns on its first line when Fullstory is off — so don't ask.
    if (!this.fullstoryEnabled) {
      return;
    }

    this.authService.isAuthenticated$
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() => this.accountAccessService.getIdentity()),
        catchError((error) => {
          // A warning, not an error: /api/user/defaults answers 404 for a user with
          // no default account or brand, which is a legitimate state and only ever
          // costs them optional product tracking.
          this.sentinelService.warn(error, 'AppComponent.identify');
          return EMPTY;
        }),
        takeUntilDestroyed(),
      )
      .subscribe((identity) => {
        void this.fullstoryService.identify(identity);
      });
  }
}
